import { useCallback, useEffect, useRef, useState } from "react"
import { trackConversion } from "@/lib/analytics"

// State + transport for the site assistant. The transcript lives only in
// this tab's sessionStorage (per origin, gone when the tab closes) and is
// sent whole on every turn — the server keeps no copy. Replies arrive as
// server-sent events over a POST, read with a ReadableStream because
// EventSource can only GET.

export type AssistantMessage = { role: "user" | "assistant"; content: string }
export type AssistantStatus = "idle" | "streaming"

const STORAGE_KEY = "site-assistant:v1"
const ENDPOINT = "/api/assistant/messages"
const HANDOFF_ENDPOINT = "/api/assistant/handoff"

type Stored = { messages: AssistantMessage[]; crisis: boolean }

function readStored(): Stored | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Stored
    return Array.isArray(parsed.messages) ? parsed : null
  } catch {
    return null
  }
}

function writeStored(value: Stored | null) {
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    else window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Private mode / storage blocked: the conversation simply won't survive a reload.
  }
}

type SseEvent = { event: string; data: Record<string, unknown> }

function parseSseChunk(chunk: string): SseEvent | null {
  let event = "message"
  const dataLines: string[] = []
  for (const line of chunk.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim()
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim())
  }
  if (dataLines.length === 0) return null
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) as Record<string, unknown> }
  } catch {
    return null
  }
}

type Options = {
  page: string
  locale: string
  unavailableText: string
}

export function useAssistant({ page, locale, unavailableText }: Options) {
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [crisis, setCrisis] = useState(false)
  const [status, setStatus] = useState<AssistantStatus>("idle")
  const [hydrated, setHydrated] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  // sessionStorage is read after mount so server rendering stays deterministic.
  useEffect(() => {
    const stored = readStored()
    if (stored) {
      setMessages(stored.messages.filter((m) => m.content.trim().length > 0))
      setCrisis(Boolean(stored.crisis))
    }
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!hydrated) return
    writeStored(messages.length > 0 ? { messages, crisis } : null)
  }, [messages, crisis, hydrated])

  useEffect(() => () => abortRef.current?.abort(), [])

  const appendToReply = useCallback((text: string) => {
    setMessages((current) => {
      const next = current.slice()
      const last = next[next.length - 1]
      if (last && last.role === "assistant") next[next.length - 1] = { ...last, content: last.content + text }
      return next
    })
  }, [])

  const replaceReply = useCallback((text: string) => {
    setMessages((current) => {
      const next = current.slice()
      const last = next[next.length - 1]
      if (last && last.role === "assistant") next[next.length - 1] = { ...last, content: text }
      return next
    })
  }, [])

  const send = useCallback(
    async (text: string) => {
      const question = text.trim()
      if (!question || status === "streaming") return

      const history: AssistantMessage[] = [
        ...messages.filter((m) => m.content.trim().length > 0),
        { role: "user", content: question },
      ]
      setMessages([...history, { role: "assistant", content: "" }])
      setStatus("streaming")
      trackConversion("assistant_question")

      const controller = new AbortController()
      abortRef.current = controller
      let received = false

      try {
        const response = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
          body: JSON.stringify({ messages: history, page, locale }),
          signal: controller.signal,
        })
        if (!response.ok || !response.body) throw new Error(`assistant ${response.status}`)

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ""
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          let boundary = buffer.indexOf("\n\n")
          while (boundary >= 0) {
            const parsed = parseSseChunk(buffer.slice(0, boundary))
            buffer = buffer.slice(boundary + 2)
            boundary = buffer.indexOf("\n\n")
            if (!parsed) continue
            if (parsed.event === "delta" && typeof parsed.data.text === "string") {
              received = true
              appendToReply(parsed.data.text)
            } else if (parsed.event === "crisis") {
              setCrisis(true)
              trackConversion("assistant_crisis")
            } else if (parsed.event === "error") {
              received = true
              replaceReply(typeof parsed.data.message === "string" ? parsed.data.message : unavailableText)
            }
          }
        }
        if (!received) replaceReply(unavailableText)
      } catch (error) {
        if ((error as Error).name === "AbortError") return
        replaceReply(unavailableText)
      } finally {
        if (abortRef.current === controller) abortRef.current = null
        setStatus("idle")
      }
    },
    [messages, status, page, locale, unavailableText, appendToReply, replaceReply],
  )

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setMessages([])
    setCrisis(false)
    setStatus("idle")
    writeStored(null)
  }, [])

  // Cross-brand continuity: park the transcript server-side for 15 minutes
  // and get a token to append to the link (the other origin can't read
  // this tab's sessionStorage).
  const createHandoff = useCallback(async (): Promise<string | null> => {
    const history = messages.filter((m) => m.content.trim().length > 0)
    if (history.length === 0) return null
    try {
      const response = await fetch(HANDOFF_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ messages: history }),
      })
      if (!response.ok) return null
      const data = (await response.json()) as { token?: string }
      return data.token ?? null
    } catch {
      return null
    }
  }, [messages])

  const loadHandoff = useCallback(async (token: string): Promise<boolean> => {
    try {
      const response = await fetch(`${HANDOFF_ENDPOINT}/${encodeURIComponent(token)}`, {
        headers: { Accept: "application/json" },
      })
      if (!response.ok) return false
      const data = (await response.json()) as { messages?: AssistantMessage[] }
      if (!Array.isArray(data.messages) || data.messages.length === 0) return false
      setMessages(data.messages)
      return true
    } catch {
      return false
    }
  }, [])

  return { messages, crisis, status, hydrated, send, reset, createHandoff, loadHandoff }
}
